import { MutableRefObject, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import SecondaryButton from "../../ui/SecondaryButton";
import { ApplicableTableSettings } from "../../tableSettings";
import SmartTable from "../../uiControls/SmartTable";
import { historyViewMainDetailTable, historyViewBillingDetailTable, historyViewRevisionTable } from "./tableSettings";
import { JobNames } from "../../framework.core/JobNames";
import Date from "../../ui/Date";
import InlineInputControlRow from '../../ui/layout/InlineInputControlRow';
import TextBox from '../../ui/TextBox';
import Float from '../../ui/Float';
import ApplicationContext from '../../ApplicationContext';
import { AcctHistHd, AcctHistoryType } from '../../fms.core/AcctHistHd';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import { ApiResult } from '../../framework.core/data/ApiResult';
import { DateRenderer, DateTimeRenderer, FloatRenderer, NullableCheckboxIconRenderer, NumberedIndicatorRenderer, TextAlignLeftRenderer, TextRenderer } from '../../uiControls/SmartTableRenderers';
import { RedDateRenderer, RedFloatRenderer, RedTextAlignLeftRenderer, RedTextRenderer } from '../../uiControls/SmartTableRenderers';
import PrimaryInverseButton from '../../ui/PrimaryInverseButton';
import useTableNavigation from '../../ui/tables/useTableNavigation';
import {  formatDateTime, getCurrentYear, getDate, getDiffDays, now } from '../../dateUtils';
import useSystemSettings from '../../hooks/useSystemSettings';
import useReportViewer from '../../hooks/useReportViewer';
import { CRDBArgs, InvoiceArgs } from '../../fms.report/args';
import { DocumentType } from '../../framework.core/DocumentType';
import useGlobalYesNoDialog from '../../hooks/useGlobalYesNoDialog';
import _ from 'lodash';

interface HistoryViewStorageKeys {
  modelStorage: string,
};

interface HistoryViewParams {
  onPopupClose?: () => void,
  jobId: number,
  jobName: JobNames,
  jobType: string | null,
};

interface HistoryViewLoadedParams {
  historyView: AcctHistHd,
  historyViewRef: MutableRefObject<AcctHistHd | null>,
  setHistoryView: (x: Partial<AcctHistHd>) => void,
  jobId: number,
  jobName: JobNames,
  jobType: string | null,
  onPopupClose?: () => void,
  fromDate:  string | null,
  toDate:  string | null,
  setFromDate: React.Dispatch<React.SetStateAction<string | null>>,
  setToDate: React.Dispatch<React.SetStateAction<string | null>>,
};

function getHistoryViewStorageKeys(): HistoryViewStorageKeys {
  return { modelStorage: 'common-history-view' }
}

function HistoryViewImpl(params: HistoryViewParams) {
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();
  
  const { modelStorage } = getHistoryViewStorageKeys();
  const { jobId, jobName, jobType, onPopupClose } = params;
  
  const [inStorage, setInStorage, removeFromStorage, storageRef] = useTypedFastStorage<AcctHistHd>(modelStorage, null);

  const defaultDate = getDate(`${getCurrentYear()}-01-01T00:00:00`);
  const [fromDate, setFromDate] = useState<string| null>(defaultDate);
  const [toDate, setToDate] = useState<string| null>(now());
  
  useEffect(() => {
    initialize();
    return () => removeFromStorage()
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobId]
  );

  async function loadExisting(): Promise<AcctHistHd | null> {
    const apiResult: ApiResult<AcctHistHd> = await appContext.api.getAcctHistory(jobName, jobId, jobType, false, fromDate, toDate);

    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }

      return null;
    }
  
    return apiResult.result;
  }

  async function initialize() {
    const data = await loadExisting();
    if (data) {
      setInStorage({ ...data });
    }
  }

  if (!inStorage) {
    return null;
  }

  return (
    <HistoryViewInitializedImpl
      historyView={inStorage}
      historyViewRef={storageRef}
      setHistoryView={setInStorage}
      onPopupClose={params.onPopupClose}
      jobId={jobId}
      jobName={jobName}
      jobType={jobType}
      fromDate={fromDate}
      toDate={toDate}
      setFromDate={setFromDate}
      setToDate={setToDate}
    />
  );
}

function HistoryViewInitializedImpl({
  historyView,
  historyViewRef,
  setHistoryView,
  onPopupClose,
  jobId,
  jobName,
  jobType,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
}: HistoryViewLoadedParams) {

  // 250620. inhyung. Focus 역순으로 하기 위해 변경
  // const [selectedRow, setSelectedRow] = useState(historyView.historyHDList.length - 1);
  const [selectedRow, setSelectedRow] = useState(0);
  const systemSettings = useSystemSettings();
  const appContext = useContext(ApplicationContext);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const { viewReport } = useReportViewer();
  const [showGlobalYesNoDialog] = useGlobalYesNoDialog();

  useTableNavigation('historyViewMainDetailTable', setSelectedRow, setSelectedRow, [], false, true);

  // 250620. inhyung. 기존 data에 isChanged flag 추가.
  const dataWithIsChanged = historyView.historyHDList.map((row, idx, arr) => {
    const prev = idx < arr.length - 1 ? arr[idx + 1] : null;
    const isChanged = row.refNo === 'Change' && prev;
    
    return {
      ...row,
      isChangedEdiId: isChanged && row.ediId !== prev.ediId,
      isChangedRefNo: isChanged,
      isChangedCurrency: isChanged && row.currency !== prev.currency,
      isChangedAmount: isChanged && row.amount !== prev.amount,
      isChangedInvoNo: isChanged && row.invoNo !== prev.invoNo,
      isChangedInvoDate: isChanged && row.invoDate !== prev.invoDate,
      isChangedCustName: isChanged && row.custName !== prev.custName,
      isChangedDueDate: isChanged && row.dueDate !== prev.dueDate,
    };
  });

  function onInvoTableInitialized() {
    const focusElement = document.getElementById(`historyViewMainDetailTable-row-${selectedRow}`) as HTMLInputElement;
    if (focusElement) {
      focusElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  function getNoLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Invoice #';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Bill #';
    }
    if (jobName === JobNames.CrDb) {
      return 'Cr/Db #';
    }

    return '';
  }

  function getToLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Invoice To';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Vendor Name';
    }
    if (jobName === JobNames.CrDb) {
      return 'Agent Name';
    }

    return '';
  }

  function getDateLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Inv. Date';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Bill Date';
    }
    if (jobName === JobNames.CrDb) {
      return 'Cr/Db Date';
    }

    return '';
  }

  /*
  function getDisplayLabel(): string {
    if ([JobNames.Invoice, JobNames.GInvo, JobNames.WHStorageInv].includes(jobName)) {
      return 'Invoice';
    }
    if ([JobNames.Bill, JobNames.GBill].includes(jobName)) {
      return 'Bill';
    }
    if (jobName === JobNames.CrDb) {
      return 'CrDb';
    }

    return '';
  }
  */

  function getAcctTypeByJobName(jobName: string) {
    switch (jobName) {
      case JobNames.Invoice:
      case JobNames.GInvo:
      case JobNames.WHStorageInv:
      case JobNames.WHInvoice:
        return AcctHistoryType.InvoHistory;
      case JobNames.Bill:
      case JobNames.GBill:
        return AcctHistoryType.ApHistory;
      case JobNames.CrDb:
        return AcctHistoryType.CrDbHistory;
    }
  }

  function PrintRenderer({ row }: any) {
    return <PrimaryInverseButton
      style={{ fontSize: '11px', paddingTop: '1px', height: '18px' }}
      onClick={() => print(row.index)}>
      Print
    </PrimaryInverseButton>
  }

  async function print(rowIndex: number) {
    let args;
    let vSubject;
    let sRInvoID = '';
    if (historyView.revisionHistList[rowIndex].id <= 0) return;


    switch (getAcctTypeByJobName(jobName)) {

      case 'InvoHistory':
        vSubject = `Invoice[${historyView.revisionHistList[rowIndex].invoNo}]`
        if (historyView.revisionHistList.length - 1 !== rowIndex) {

          if (rowIndex === 0) {
            sRInvoID = historyView.revisionHistList[rowIndex].id.toString();
          } else {
            sRInvoID = historyView.revisionHistList[rowIndex - 1].id.toString() + "," + historyView.revisionHistList[rowIndex].id.toString();
          }    
          args = new InvoiceArgs({
            docType: DocumentType.Invoice,
            formCaption: vSubject,
            subject: vSubject,
            stamp: appContext.suntekSettings?.accounting?.showPastDueOnInv,
            useMultiCur: appContext.suntekSettings?.accounting?.showCurAmountInInv,
            toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_RINVOHD', 'F_BillTo')}`,
            tbName: 'T_RINVOHD',
            tbId: 'R' + sRInvoID,
        newDesignForm: systemSettings.useNewInvoiceReport ?? false,
          });
          viewReport(args, "", true);
        } else {

          const printMsg = await showGlobalYesNoDialog(
            <div>
              <h3>Do you want to Print?</h3>
            </div>
          ,false, 0, true);

          if (printMsg) {
            args = new InvoiceArgs({
              docType: DocumentType.Invoice,
              formCaption: vSubject,
              subject: vSubject,
              stamp: appContext.suntekSettings?.accounting?.showPastDueOnInv,
              useMultiCur: appContext.suntekSettings?.accounting?.showCurAmountInInv,
              toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_INVOHD', 'F_BillTo')}`,
              tbName: 'T_RINVOHD',
              tbId: historyView.revisionHistList[rowIndex].id.toString(),
        newDesignForm: systemSettings.useNewInvoiceReport ?? false,
            });
            viewReport(args, "", true);
          }

        }
        break;

      case 'CrDbHistory':
        vSubject = `Cr/Db Note [CrDb No: ${historyView.revisionHistList[rowIndex].invoNo}]`;
        if (historyView.revisionHistList.length - 1 !== rowIndex) {
          args = new CRDBArgs({
            docType: DocumentType.CRDB,
            formCaption: vSubject,
            subject: vSubject,
            toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_RCRDBHD', 'F_Agent')}`,
            tbName: 'T_RINVOHD',
            tbId: 'R' + historyView.revisionHistList[rowIndex].id.toString(),
            newDesignForm: appContext.systemSettings?.useNewCrDbReport ?? false,
          });
          viewReport(args, "", true);
        } else {

          const printMsg = await showGlobalYesNoDialog(
            <div>
              <h3>Do you want to print?</h3>
            </div>
          ,false, 0, true);

          if (printMsg) {
            args = new CRDBArgs({
              docType: DocumentType.CRDB,
              formCaption: vSubject,
              subject: vSubject,
              toCompanyid: `${await appContext.api.getRList(historyView.revisionHistList[rowIndex].id.toString(), 'T_CRDBHD', 'F_Agent')}`,
              tbName: 'T_RINVOHD',
              tbId: historyView.revisionHistList[rowIndex].id.toString(),
              newDesignForm: appContext.systemSettings?.useNewCrDbReport ?? false,
            });
            viewReport(args, "", true);
          }
        }
        break;
    }
  }

  function validAllDeleteHistoryDate() {
    if (!fromDate || !toDate) return;
  
    if (getDiffDays(toDate, fromDate) < 0) {
      showGlobalOkDialog("Start date cannot be set later than end date.");
      return;
    }
  }

  async function loadHistorysearchData() {
    validAllDeleteHistoryDate();

    const apiResult: ApiResult<AcctHistHd> = await appContext.api.getAcctHistory(jobName, jobId, jobType, false, fromDate, toDate);

    if (!apiResult || !apiResult.success) {
      await showGlobalOkDialog(apiResult?.message || 'Cannot load data');
      if (onPopupClose) {
        onPopupClose();
      }

      return null;
    }

    setHistoryView({
      ...apiResult.result,
      fromDate,
      toDate
    });
  }

  const mainTableOptions: ApplicableTableSettings = {
    ...historyViewMainDetailTable,
    height: '120px',
    selectable: true,
    rowProps: (row: any) => ({ onClick: () => setSelectedRow(row.index) }),
    selectedRowIndex: selectedRow,
    customTableId: 'historyViewMainDetailTable',
    tableName: 'historyViewMainDetailTable',
    // data: historyView.historyHDList,
    data: dataWithIsChanged,

    // 250626. inhyung. Date는 Red 표시 제외 및 컬럼 Title 수정.
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer},
      { accessor: 'ediDate', Cell: DateTimeRenderer},
      { accessor: 'ediId', Cell: (props: any) => props.row.original.isChangedEdiId ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, },
      { accessor: 'refNo', Cell: (props: any) => props.row.original.isChangedRefNo ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, },
      { accessor: 'currency', Cell: (props: any) => props.row.original.isChangedCurrency ? <RedTextRenderer {...props} /> : <TextRenderer {...props} />, },
      { accessor: 'amount', Cell: (props: any) => props.row.original.isChangedAmount ? <RedFloatRenderer {...props} /> : <FloatRenderer {...props} />, },
      { accessor: 'invoNo', Cell: (props: any) => props.row.original.isChangedInvoNo ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, Header: () => <span>{getNoLabel()}</span>},
      { accessor: 'isDraft', Cell: NullableCheckboxIconRenderer, },
      { accessor: 'invoDate', Cell: (props: any) => props.row.original.isChangedInvoDate ? <RedDateRenderer {...props} /> : <DateRenderer {...props} />, Header: () => <span>{getDateLabel()}</span> },
      { accessor: 'custName', Cell: (props: any) => props.row.original.isChangedCustName ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} />, Header: () => <span>{getToLabel()}</span>},
      { accessor: 'dueDate', Cell: (props: any) => props.row.original.isChangedDueDate ? <RedDateRenderer {...props} /> : <DateRenderer {...props} />, },
      { accessor: 'custName', Cell: (props: any) => props.row.original.isChangedCustName ? <RedTextAlignLeftRenderer {...props} /> : <TextAlignLeftRenderer {...props} /> },
    ],
    isModalTable: true,
  };

  const billingDetailTableSettings = { ...historyViewBillingDetailTable, columns: historyViewBillingDetailTable.columns.map(c => ({ ...c })) };
  const columns = _.keyBy(billingDetailTableSettings.columns, 'accessor');
  let hcolPercent = 0
  if (jobName === JobNames.CrDb) {
    const removeColumns = ['currency'];
    for (const accessor in columns) {
      if (removeColumns.indexOf(accessor) >= 0) {
        hcolPercent += columns[accessor].initialWidthPercentage ?? 0;
        billingDetailTableSettings.columns.splice(billingDetailTableSettings.columns.indexOf(columns[accessor]), 1);
      }
    }

    columns['description'].initialWidthPercentage = (columns['description'].initialWidthPercentage ?? 0) + hcolPercent;
  }
  
  const billingDetailTableOptions: ApplicableTableSettings = {
    ...billingDetailTableSettings,
    height: '120px',
    selectable: true,
    tableName: 'historyViewBillingDetailTable',
    data: selectedRow >= 0 ? historyView.historyDetaillAllList.filter(x => x.ediHdId === historyView.historyHDList[selectedRow].id) : [],
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      { accessor: 'seqNo', Cell: NumberedIndicatorRenderer },
      { accessor: 'chgTypeDesc', Cell: TextAlignLeftRenderer },
      { accessor: 'code', Cell: TextAlignLeftRenderer },
      { accessor: 'description', Cell: TextAlignLeftRenderer },
      { accessor: 'currency', Cell: TextRenderer },
      { accessor: 'amount', Cell: FloatRenderer },
    ],
    isModalTable: true,
  };

  const revisionTableOptions: ApplicableTableSettings = {
    ...historyViewRevisionTable,
    selectable: true,
    tableName: 'historyViewRevisionTable',
    height: '273px',
    data: historyView.revisionHistList,
    rowRenderers: [
      { accessor: 'indicator', Cell: NumberedIndicatorRenderer },
      //  { accessor: 'printa', Cell: () => <PrimaryInverseButton style={{fontSize: '11px', paddingTop: '3px', paddingBottom: '3px', height: '18px'}} >Print</PrimaryInverseButton>},
      { accessor: 'printa', Cell: PrintRenderer },
      { accessor: 'u2Date', Cell: DateRenderer },
      { accessor: 'invoNo', Cell: TextAlignLeftRenderer, Header: () => <span>{getNoLabel()}</span> },
    ],
    isModalTable: true,
  };

  const isRecogField = useMemo(() => { 
    if (["OIM", "OIH", "OEM", "OEH", "AIM", "AIH", "AEM", "AEH", "OTH"].includes(jobType ?? '')) {
      return true;
    } else {
      return false;
    }
  }, [jobType])

  // 250724.inhyung.issue#2576 UI 추가 및 위치 변경
  return (
    <div className='report-popup'>
        <>
          <div style={{ display: 'flex' }}>
            <div style={{ width: '290px' }}>
              <InlineInputControlRow label={getNoLabel()} mode="w100">
                <div style={{ width: '100px' }}>
                  <TextBox value={historyView.invoiceNo} disabled isModal />
                </div>
              </InlineInputControlRow>
            </div>

          <div style={{ width: '160px', marginLeft: '20px' }}>
            <InlineInputControlRow label={getDateLabel()} mode="w80">
              <Date value={historyView.invoiceDate} disabled />
            </InlineInputControlRow>
          </div>

          <div style={{ width: '160px', marginLeft: '20px' }}>
            <InlineInputControlRow label="Amount" mode="w80">
              <Float value={historyView.amount} disabled isModal />
            </InlineInputControlRow>
          </div>

          {isRecogField && (
            <div style={{ width: '180px', marginLeft: '35px' }}>
              <InlineInputControlRow label="Recog Date" mode="w80">
                <div style={{ width: '80px' }}>
                  <Date value={historyView.confirmDate} disabled />
                </div>
              </InlineInputControlRow>
            </div>
          )}
        </div>

          <div style={{ display: 'flex' }}>
            <div style={{ width: '290px' }}>
              <InlineInputControlRow label={getToLabel()} mode="w100">
                <TextBox value={historyView.billName} disabled isModal />
              </InlineInputControlRow>
            </div>

          <div style={{ width: '160px', marginLeft: '20px' }}>
            <InlineInputControlRow label="Due Date" mode="w80">
              <Date value={historyView.dueDate} disabled />
            </InlineInputControlRow>
          </div>

          <div style={{ width: '160px', marginLeft: '20px' }}>
            <InlineInputControlRow label="Paid Amount" mode="w80">
              <Float value={historyView.paidAmt} disabled isModal />
            </InlineInputControlRow>
          </div>

          {isRecogField && (
            <div style={{ width: '180px', marginLeft: '35px' }}>
              <InlineInputControlRow label="R. Action Date" mode="w80">
                <TextBox
                  placeholder={systemSettings?.dateTimePlaceHolder ?? ''}
                  disabled
                  value={formatDateTime(historyView.confirmActionDate)}
                />
              </InlineInputControlRow>
            </div>
          )}
        </div>

        <div style={{ display: 'flex' }}>
          <div
            style={{
              width: '290px',
              visibility: jobName === JobNames.CrDb ? 'visible' : 'hidden',
            }}
          >
            <InlineInputControlRow label="Agent Ref.#" mode="w100">
              <TextBox value={historyView.agentRefNo} disabled isModal />
            </InlineInputControlRow>
          </div>

          <div
            style={{
              width: '175px',
              marginLeft: '20px',
              visibility:
                jobName !== JobNames.GInvo && jobName !== JobNames.GBill
                  ? 'visible'
                  : 'hidden',
            }}
          >
            <InlineInputControlRow label="Ref.#" mode="w80">
               <TextBox value={historyView.refNo} disabled isModal />
            </InlineInputControlRow>
          </div>

          <div
            style={{
              width: '185px',
              marginLeft: '5px',
              visibility:
                jobName !== JobNames.GInvo && jobName !== JobNames.GBill
                  ? 'visible'
                  : 'hidden',
            }}
          >
            <InlineInputControlRow label="MBL#" mode="w80">
              <TextBox value={historyView.mblNo} disabled isModal/>
            </InlineInputControlRow>
          </div>

          <div
            style={{
              width: '180px',
              marginLeft: '10px',
              visibility:
                jobName !== JobNames.GInvo && jobName !== JobNames.GBill
                  ? 'visible'
                  : 'hidden',
            }}
          >
            <InlineInputControlRow label="HBL#" mode="w80">
              <TextBox value={historyView.hblNo} disabled isModal/>
            </InlineInputControlRow>
          </div>
        </div>

        <div style={{ display: 'flex' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ width: '865px' }}>
              <SmartTable
                {...mainTableOptions}
                onInitialized={onInvoTableInitialized}
              />
              <div style={{ marginTop: '10px', marginBottom: '4px' }}>
                Billing Detail
              </div>

              <div style={{ display: 'flex' }}>
                <div style={{ width: '675px' }}>
                  <SmartTable {...billingDetailTableOptions} />
                </div>

                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginLeft: '20px',
                    justifyContent: 'flex-start',
                    marginTop: '77px',
                  }}
                >
                  <div style={{ width: '150px' }}>
                    <InlineInputControlRow label="Update" mode="w50">
                      <TextBox
                        value={formatDateTime(historyView.updatedDate)}
                        disabled
                      />
                    </InlineInputControlRow>
                  </div>
                  <div style={{ width: '150px' }}>
                    <InlineInputControlRow label="Create" mode="w50">
                      <TextBox
                        value={formatDateTime(historyView.createdDate)}
                        disabled
                      />
                    </InlineInputControlRow>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(jobName !== JobNames.Bill && jobName !== JobNames.GBill) && (
            <div style={{ width: '240px', marginLeft: '45px' }}>
              <SmartTable {...revisionTableOptions} />
            </div>
          )}
        </div>

        <div className='report-footer'>
          <SecondaryButton
            size="regular"
            style={{ width: '90px' }}
            onClick={onPopupClose}
          >
            CLOSE
          </SecondaryButton>
        </div>
      </>
    </div>
  );
}

      {/* jhlee 250109 - Delete History popup -> detail로 변경 */}
      {/* {viewDeleteHistory &&
        <div style={{ display: 'flex', marginTop: '-20px' }}>
          <div style={{ width: '840px' }}>
            <>&nbsp;</>

            <InlineInputControlRow label='Date' mode='small'>
              <div style={{ display: 'flex', marginLeft: '-75px' }}>
                <div style={{ width: '80px' }}>
                  <Date value={historyView.fromDate ?? defaultDate} onChange={(selectedFromDate) => {
                    if (selectedFromDate && compareIso(getDate(selectedFromDate) ?? '', getDate(now()) ?? '') > 0) {
                      showGlobalOkDialog('You cannot enter future date.');
                      setHistoryView({ fromDate: defaultDate });
                    } else {
                        setHistoryView({ fromDate: selectedFromDate })};
                        setFromDate(selectedFromDate);
                      } 
                    }
                  />    
                </div>

                <div style={{ width: '60px', paddingLeft: '32px' }}>
                  ~
                </div>

                <div style={{ width: '80px' }}>
                  <Date value={historyView.toDate ?? now()}
                        onChange={(selectedToDate) => {
                          if (selectedToDate && compareIso(getDate(selectedToDate) ?? '', getDate(now()) ?? '') > 0) {
                          showGlobalOkDialog("You cannot enter future date.");
                          setHistoryView({ toDate: now() });
                        } else {
                          setHistoryView({ toDate: selectedToDate })};
                          setToDate(selectedToDate)
                        }
                      }
                  />
                </div>

                <div className='ml-4'>
                  <SecondaryButton focusable={true} style={{ width: 60, marginRight: 80 }} size='extrasmall' onClick={async () => { await loadHistorysearchData() }}>List</SecondaryButton>
                </div>
              </div>
            </InlineInputControlRow>

            <div>
              <SmartTable {...mainTableOptions} onInitialized={onInvoTableInitialized} />
            </div>

            <div style={{ marginTop: '10px', display: 'flex' }}>
              <div style={{ width: '190px', textAlign: 'right', paddingRight: '10px' }}>
                Billing Detail
              </div>
              <div style={{ width: '650px' }}>
                <SmartTable ref={billingTableRef} {...billingDetailTableOptions} onInitialized={onBillingTableInitialized} />
              </div>
            </div>
          </div>

          {(jobName !== JobNames.Bill && jobName !== JobNames.GBill && !viewDeleteHistory) &&
            <div style={{ width: '240px', marginLeft: '40px', marginTop: '16px' }}>
              <SmartTable {...revisionTableOptions} />
            </div>
          }
        </div>
      } */}

const HistoryViewDetails = ({
  onPopupClose,
  selectedElement,
  jobId,
  jobName,
  jobType,
}: { onPopupClose?: () => void, selectedElement?: any, jobId: number, jobName: JobNames, jobType: string | null }) => {
  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
   */

  if (selectedElement) {
    jobId = selectedElement.id;
    switch (jobName) {
      case JobNames.OpInvoice:
      case JobNames.Invoice:
        jobName = JobNames.Invoice;
        break;

      case JobNames.OpBill:
      case JobNames.Bill:
        jobName = JobNames.Bill;
        break;
      case JobNames.OpCrDb:
      case JobNames.CrDb:
        jobName = JobNames.CrDb;
        break;
    }
    jobType = selectedElement.jobType;
  }

  return (
    <HistoryViewImpl jobName={jobName} jobType={jobType} jobId={jobId} onPopupClose={onPopupClose} key={`historyView-details`} />
  );
};
export default HistoryViewDetails;
